import { useEffect, useState } from 'react';
import { Layout } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader,
  DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertTriangle, Plus, Loader2, Calendar, Clock,
} from 'lucide-react';
import { aiActApi } from '../../lib/api';
import { toast } from 'sonner';
import { cn, formatDate } from '../../lib/utils';

const TYPES = [
  { value: 'serious_incident', label: 'Incidente Grave (Art. 72)', notify: true },
  { value: 'malfunction', label: 'Avaria / Mau funcionamento', notify: false },
  { value: 'near_miss', label: 'Quase-incidente', notify: false },
];

const SEVERITIES = [
  { value: 'critical', label: 'Crítica', color: 'bg-red-50 text-red-700 border-red-200' },
  { value: 'high', label: 'Alta', color: 'bg-orange-50 text-orange-700 border-orange-200' },
  { value: 'medium', label: 'Média', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 'low', label: 'Baixa', color: 'bg-blue-50 text-blue-700 border-blue-200' },
];

const NOTIF_STATUSES = [
  { value: 'pending', label: 'Pendente' },
  { value: 'notified', label: 'Notificado' },
  { value: 'closed', label: 'Encerrado' },
];

const init = { system_id: '', incident_type: 'malfunction', description: '', affected_users: '', severity: 'medium', nis2_incident_ref: '' };

export default function AiIncidents() {
  const [incidents, setIncidents] = useState([]);
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(init);
  const [saving, setSaving] = useState(false);
  const [updating, setUpdating] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [inc, sy] = await Promise.all([aiActApi.listIncidents({}), aiActApi.listSystems({})]);
      setIncidents(inc.data);
      setSystems(sy.data);
    } catch { toast.error('Erro ao carregar incidentes IA'); }
    finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) { toast.error('Descrição obrigatória'); return; }
    setSaving(true);
    try {
      const payload = { ...form, system_id: form.system_id || null };
      await aiActApi.createIncident(payload);
      toast.success('Incidente IA registado');
      setShowForm(false); setForm(init); load();
    } catch { toast.error('Erro ao guardar'); }
    finally { setSaving(false); }
  };

  const setNotifStatus = async (inc, status) => {
    setUpdating(inc.ai_incident_id);
    try {
      const payload = { notification_status: status };
      if (status === 'notified') payload.notification_date = new Date().toISOString();
      await aiActApi.updateIncident(inc.ai_incident_id, payload);
      toast.success('Estado atualizado');
      load();
    } catch { toast.error('Erro ao atualizar'); }
    finally { setUpdating(null); }
  };

  const getSysName = (sid) => sid ? (systems.find(s => s.system_id === sid)?.name || sid) : '—';
  const sevCfg = (s) => SEVERITIES.find(x => x.value === s) || SEVERITIES[2];

  if (loading) return <Layout title="EU AI Act — Incidentes"><div className="flex justify-center h-64 items-center"><Loader2 className="h-8 w-8 animate-spin" /></div></Layout>;

  return (
    <Layout title="EU AI Act — Incidentes (Art. 72)" headerContent={
      <Button onClick={() => { setForm(init); setShowForm(true); }} data-testid="new-ai-incident-btn">
        <Plus className="h-4 w-4 mr-2" />Novo Incidente IA
      </Button>
    }>
      <div className="space-y-6" data-testid="ai-incidents-container">

        <Card className="card-flat bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">Art. 72 — Notificação de Incidentes Graves</p>
                <p className="text-sm text-blue-700 mt-1">
                  Incidentes graves em sistemas IA de alto risco devem ser notificados à autoridade nacional num prazo máximo de <strong>15 dias</strong>.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {incidents.length === 0 ? (
          <Card className="card-flat"><CardContent className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Nenhum incidente IA registado</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-3" data-testid="ai-incidents-list">
            {incidents.map(inc => {
              const sCfg = sevCfg(inc.severity);
              const typeCfg = TYPES.find(t => t.value === inc.incident_type);
              return (
                <Card key={inc.ai_incident_id} className="card-flat hover:shadow-sm transition-shadow" data-testid={`aincident-${inc.ai_incident_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className={cn('h-12 w-12 rounded-lg flex items-center justify-center', sCfg.color)}>
                        <AlertTriangle className="h-6 w-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-mono text-xs text-slate-400">{inc.ai_incident_id}</span>
                          <Badge variant="outline" className="text-xs">{typeCfg?.label || inc.incident_type}</Badge>
                          <Badge variant="outline" className={cn('text-xs', sCfg.color)}>{sCfg.label}</Badge>
                          {inc.notification_required && (
                            <Badge variant="outline" className="text-xs bg-red-100 text-red-800 border-red-300">
                              <Clock className="h-3 w-3 mr-1" />Notif. Art. 72
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-900 line-clamp-2">{inc.description}</p>
                        <div className="flex items-center gap-3 text-xs text-slate-500 mt-2 flex-wrap">
                          {inc.system_id && <span>Sistema: {getSysName(inc.system_id)}</span>}
                          <span>Detetado: {formatDate(inc.created_at)}</span>
                          {inc.notification_deadline && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Prazo: {inc.notification_deadline}
                            </span>
                          )}
                          {inc.affected_users && <span>Afetados: {inc.affected_users}</span>}
                        </div>
                      </div>
                      <Select value={inc.notification_status} onValueChange={v => setNotifStatus(inc, v)} disabled={updating === inc.ai_incident_id}>
                        <SelectTrigger className="w-[140px]" data-testid={`status-${inc.ai_incident_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>{NOTIF_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Novo Incidente IA</DialogTitle>
            <DialogDescription>Registo no âmbito do Art. 72 — notificação de incidentes IA</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label>Sistema IA (opcional)</Label>
              <Select value={form.system_id || 'none'} onValueChange={v => setForm({ ...form, system_id: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="aincident-system"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum</SelectItem>
                  {systems.map(s => <SelectItem key={s.system_id} value={s.system_id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={form.incident_type} onValueChange={v => setForm({ ...form, incident_type: v })}>
                  <SelectTrigger data-testid="aincident-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Severidade</Label>
                <Select value={form.severity} onValueChange={v => setForm({ ...form, severity: v })}>
                  <SelectTrigger data-testid="aincident-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>{SEVERITIES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descrição *</Label>
              <Textarea rows={4} required value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="aincident-desc" />
            </div>
            <div className="space-y-2">
              <Label>Utilizadores afetados</Label>
              <Input value={form.affected_users} onChange={e => setForm({ ...form, affected_users: e.target.value })} placeholder="Ex: 1500 utilizadores finais, área Sul" />
            </div>
            <div className="space-y-2">
              <Label>Referência NIS2 (opcional)</Label>
              <Input value={form.nis2_incident_ref} onChange={e => setForm({ ...form, nis2_incident_ref: e.target.value })} placeholder="INC-XXXX" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="save-aincident-btn">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Registar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
